import { useEffect, useMemo, useState } from "react";
import type { PlatformClient } from "../../platform/supabase";

type DirectoryRow={
  learnerId:string;
  displayName:string;
  gradeLevel?:string|null;
  classLabel?:string|null;
  academicYear?:string|null;
  journeyId:string;
  journeyStatus:string;
  journeyStartedAt:string;
  activeGoalCount:number;
  nextActionCount:number;
  latestActivityAt?:string|null;
};

type Goal={
  goalId:string;
  supersedesId?:string|null;
  goalType:string;
  status:string;
  title:string;
  description?:string|null;
  targetOn?:string|null;
  effectiveAt:string;
  isLatest:boolean;
};

type Entry={
  entryId:string;
  supersedesId?:string|null;
  entryType:string;
  title:string;
  body?:string|null;
  visibility:string;
  occurredAt:string;
  createdByPersonId:string;
  createdByName:string;
  isLatest:boolean;
};

type Enrollment={
  enrollmentId:string;
  schoolId:string;
  schoolName:string;
  academicYear?:string|null;
  gradeLevel?:string|null;
  classLabel?:string|null;
  status:string;
  enrolledOn?:string|null;
  endedOn?:string|null;
};

type TimelineItem={
  occurredAt:string;
  eventType:string;
  resourceId:string;
  title:string;
  description?:string|null;
  visibility:string;
  metadata:Record<string,unknown>;
};

type Detail={
  learner:{
    learnerId:string;
    displayName:string;
    journeyId:string;
    journeyStatus:string;
    journeyStartedAt:string;
  };
  enrollments:Enrollment[];
  goals:Goal[];
  entries:Entry[];
  timeline:TimelineItem[];
};

type EntryType="TRANSITION_POINT"|"NEXT_ACTION"|"TEACHER_COMMENT"|"MILESTONE";

function formatDate(value?:string|null){
  if(!value)return "-";
  return new Date(value).toLocaleDateString("ko-KR",{
    year:"numeric",month:"2-digit",day:"2-digit"
  });
}

function formatDateTime(value?:string|null){
  if(!value)return "-";
  return new Date(value).toLocaleString("ko-KR",{
    year:"numeric",month:"2-digit",day:"2-digit",hour:"2-digit",minute:"2-digit"
  });
}

function goalTypeLabel(value:string){
  const map:Record<string,string>={
    EXPLORATION:"탐색",
    LEARNING:"학습",
    EXPERIENCE:"경험",
    EDUCATION:"진학",
    CAREER:"진로",
    OTHER:"기타"
  };
  return map[value] || value;
}

function goalStatusLabel(value:string){
  const map:Record<string,string>={
    DRAFT:"초안",
    ACTIVE:"진행 중",
    ACHIEVED:"달성",
    PAUSED:"잠시 멈춤",
    RETIRED:"종료"
  };
  return map[value] || value;
}

function entryTypeLabel(value:string){
  const map:Record<string,string>={
    TRANSITION_POINT:"전환점",
    NEXT_ACTION:"다음 행동",
    TEACHER_COMMENT:"교사 메모",
    MILESTONE:"마일스톤"
  };
  return map[value] || value;
}

function timelineLabel(value:string){
  const map:Record<string,string>={
    ENROLLMENT:"학년도",
    GOAL:"목표",
    TRANSITION_POINT:"전환점",
    NEXT_ACTION:"다음 행동",
    TEACHER_COMMENT:"교사 메모",
    MILESTONE:"마일스톤",
    EXPERIENCE:"경험",
    ASSESSMENT:"검사",
    EVIDENCE:"증빙",
    EVALUATION:"평가",
    COMPETENCY:"역량",
    ACHIEVEMENT:"성취",
    CREDENTIAL:"자격증명"
  };
  return map[value] || value;
}

export function CenterJourneysPage({
  client,
  schoolId
}:{
  client:PlatformClient;
  schoolId:string;
}){
  const [directory,setDirectory]=useState<DirectoryRow[]>([]);
  const [selectedLearnerId,setSelectedLearnerId]=useState("");
  const [detail,setDetail]=useState<Detail|null>(null);
  const [busy,setBusy]=useState("");
  const [error,setError]=useState("");
  const [refresh,setRefresh]=useState(0);
  const [query,setQuery]=useState("");

  const [goalType,setGoalType]=useState("EXPLORATION");
  const [goalTitle,setGoalTitle]=useState("");
  const [goalDescription,setGoalDescription]=useState("");
  const [goalTarget,setGoalTarget]=useState("");

  const [entryType,setEntryType]=useState<EntryType>("NEXT_ACTION");
  const [entryTitle,setEntryTitle]=useState("");
  const [entryBody,setEntryBody]=useState("");
  const [entryVisibility,setEntryVisibility]=useState("LEARNER_AND_STAFF");

  useEffect(()=>{
    let active=true;
    client.rpc("center_journey_directory",{p_school_id:schoolId}).then(({data,error})=>{
      if(!active)return;
      if(error){setError(error.message);return;}
      const rows=(data || []) as DirectoryRow[];
      setDirectory(rows);
      setSelectedLearnerId((current)=>
        rows.some((item)=>item.learnerId===current)
          ? current
          : rows[0]?.learnerId || ""
      );
    });
    return()=>{active=false;};
  },[client,schoolId,refresh]);

  useEffect(()=>{
    if(!selectedLearnerId){setDetail(null);return;}
    let active=true;
    setError("");
    client.rpc("center_journey_detail",{
      p_school_id:schoolId,
      p_learner_id:selectedLearnerId
    }).then(({data,error})=>{
      if(!active)return;
      if(error){setError(error.message);return;}
      setDetail(data as Detail);
    });
    return()=>{active=false;};
  },[client,schoolId,selectedLearnerId,refresh]);

  const filtered=useMemo(()=>{
    const value=query.trim().toLowerCase();
    if(!value)return directory;
    return directory.filter((item)=>
      item.displayName.toLowerCase().includes(value)
      || (item.gradeLevel || "").toLowerCase().includes(value)
      || (item.classLabel || "").toLowerCase().includes(value)
    );
  },[directory,query]);

  const currentGoals=useMemo(
    ()=>detail?.goals.filter((item)=>item.isLatest) || [],
    [detail]
  );

  const currentEntries=useMemo(
    ()=>detail?.entries.filter((item)=>item.isLatest) || [],
    [detail]
  );

  const nextActions=currentEntries.filter((item)=>item.entryType==="NEXT_ACTION");
  const teacherComments=currentEntries.filter((item)=>item.entryType==="TEACHER_COMMENT");

  async function run(
    key:string,
    action:()=>PromiseLike<{data?:unknown;error?:{message:string}|null}>
  ){
    setBusy(key);setError("");
    try{
      const result=await action();
      if(result.error)throw new Error(result.error.message);
      setRefresh((value)=>value+1);
      return result.data;
    }catch(err){
      setError(err instanceof Error ? err.message : "처리 중 오류가 발생했습니다.");
      return null;
    }finally{
      setBusy("");
    }
  }

  async function createGoal(){
    if(!goalTitle.trim())return;
    const id=await run("goal-create",()=>client.rpc("center_create_career_goal",{
      p_school_id:schoolId,
      p_learner_id:selectedLearnerId,
      p_goal_type:goalType,
      p_title:goalTitle.trim(),
      p_description:goalDescription.trim() || null,
      p_target_on:goalTarget || null,
      p_status:"ACTIVE"
    }));
    if(id){
      setGoalTitle("");
      setGoalDescription("");
      setGoalTarget("");
    }
  }

  async function changeGoalStatus(goal:Goal,status:string){
    await run("goal-status-"+goal.goalId,()=>client.rpc("center_supersede_career_goal",{
      p_school_id:schoolId,
      p_goal_id:goal.goalId,
      p_status:status,
      p_title:goal.title,
      p_description:goal.description || null,
      p_target_on:goal.targetOn || null
    }));
  }

  async function createEntry(){
    if(!entryTitle.trim())return;
    const id=await run("entry-create",()=>client.rpc("center_add_journey_entry",{
      p_school_id:schoolId,
      p_learner_id:selectedLearnerId,
      p_entry_type:entryType,
      p_title:entryTitle.trim(),
      p_body:entryBody.trim() || null,
      p_visibility:entryType==="TEACHER_COMMENT" ? "STAFF_ONLY" : entryVisibility,
      p_occurred_at:null
    }));
    if(id){
      setEntryTitle("");
      setEntryBody("");
    }
  }

  if(!directory.length && !error){
    return (
      <section className="journey-page">
        <div className="section-heading">
          <div>
            <p className="eyebrow">Longitudinal Career Journey</p>
            <h2>진로성장 여정</h2>
          </div>
          <span className="phase-badge">BUILD-11</span>
        </div>
        <p className="case-empty">담당 학생이 없습니다.</p>
      </section>
    );
  }

  return (
    <section className="journey-page">
      <div className="section-heading">
        <div>
          <p className="eyebrow">Longitudinal Career Journey</p>
          <h2>진로성장 여정</h2>
          <p className="route-description">
            검사·프로그램·증빙·평가·역량·성취·자격증명을 한 학생의 시간축에서 이어 봅니다.
          </p>
        </div>
        <span className="phase-badge">BUILD-11</span>
      </div>

      {error && <p className="error">{error}</p>}

      <div className="journey-layout">
        <aside className="journey-student-panel">
          <div className="journey-search">
            <input value={query} onChange={(event)=>setQuery(event.target.value)} placeholder="학생 검색" />
          </div>
          <div className="panel-title">
            <strong>학생</strong>
            <span>{filtered.length}명</span>
          </div>
          {filtered.map((item)=>(
            <button
              key={item.learnerId}
              className={selectedLearnerId===item.learnerId ? "journey-student-row active" : "journey-student-row"}
              onClick={()=>setSelectedLearnerId(item.learnerId)}
            >
              <strong>{item.displayName}</strong>
              <span>
                {item.gradeLevel || "-"}학년 {item.classLabel || ""}반
                {item.academicYear ? " · "+item.academicYear : ""}
              </span>
              <small>
                목표 {item.activeGoalCount} · 다음 행동 {item.nextActionCount}
              </small>
            </button>
          ))}
        </aside>

        <div className="journey-main">
          {!detail ? <p className="muted">학생의 진로성장 여정을 불러오는 중…</p> : (
            <>
              <header className="journey-hero">
                <div>
                  <p className="eyebrow">Career Journey</p>
                  <h2>{detail.learner.displayName}</h2>
                  <p>
                    {detail.enrollments.at(-1)?.academicYear || "학년도 미정"}
                    {" · "}
                    {detail.enrollments.at(-1)?.gradeLevel || "-"}학년
                    {detail.enrollments.at(-1)?.classLabel ? " "+detail.enrollments.at(-1)?.classLabel+"반" : ""}
                    {" · "}
                    여정 시작 {formatDate(detail.learner.journeyStartedAt)}
                  </p>
                </div>
                <span className="status-pill good">진로성장 여정 진행 중</span>
              </header>

              <div className="journey-overview-grid">
                <JourneyMetric label="학년도" value={detail.enrollments.length} />
                <JourneyMetric label="목표 이력" value={detail.goals.length} />
                <JourneyMetric label="현재 다음 행동" value={nextActions.length} />
                <JourneyMetric label="Timeline" value={detail.timeline.length} />
              </div>

              <div className="journey-editor-grid">
                <section className="journey-editor-card">
                  <div className="panel-title">
                    <strong>목표</strong>
                    <span>수정 대신 이력으로 이어집니다</span>
                  </div>
                  <div className="journey-form">
                    <label>
                      유형
                      <select value={goalType} onChange={(event)=>setGoalType(event.target.value)}>
                        <option value="EXPLORATION">탐색</option>
                        <option value="LEARNING">학습</option>
                        <option value="EXPERIENCE">경험</option>
                        <option value="EDUCATION">진학</option>
                        <option value="CAREER">진로</option>
                        <option value="OTHER">기타</option>
                      </select>
                    </label>
                    <label>
                      목표일
                      <input type="date" value={goalTarget} onChange={(event)=>setGoalTarget(event.target.value)} />
                    </label>
                    <label className="wide">
                      목표
                      <input value={goalTitle} onChange={(event)=>setGoalTitle(event.target.value)} placeholder="이번 학기에 경험으로 확인할 목표" />
                    </label>
                    <label className="wide">
                      설명
                      <textarea value={goalDescription} onChange={(event)=>setGoalDescription(event.target.value)} />
                    </label>
                    <button className="primary compact-button" disabled={busy!=="" || !goalTitle.trim()} onClick={createGoal}>
                      목표 추가
                    </button>
                  </div>

                  <div className="journey-goal-list">
                    {currentGoals.map((goal)=>(
                      <article className="journey-goal-card" key={goal.goalId}>
                        <div>
                          <span className="journey-type-chip">{goalTypeLabel(goal.goalType)}</span>
                          <strong>{goal.title}</strong>
                          <p>{goal.description || "설명 없음"}</p>
                          <small>목표일 {formatDate(goal.targetOn)} · {goalStatusLabel(goal.status)}</small>
                        </div>
                        {goal.status==="ACTIVE" && (
                          <div className="journey-card-actions">
                            <button className="secondary" disabled={busy!==""} onClick={()=>changeGoalStatus(goal,"PAUSED")}>잠시 멈춤</button>
                            <button className="primary compact-button" disabled={busy!==""} onClick={()=>changeGoalStatus(goal,"ACHIEVED")}>달성</button>
                          </div>
                        )}
                        {goal.status==="PAUSED" && (
                          <button className="secondary" disabled={busy!==""} onClick={()=>changeGoalStatus(goal,"ACTIVE")}>다시 시작</button>
                        )}
                      </article>
                    ))}
                    {!currentGoals.length && <p className="case-empty">현재 목표가 없습니다.</p>}
                  </div>
                </section>

                <section className="journey-editor-card">
                  <div className="panel-title">
                    <strong>전환점·다음 행동</strong>
                    <span>교사 메모는 학생에게 비공개</span>
                  </div>
                  <div className="journey-form">
                    <label>
                      기록 유형
                      <select value={entryType} onChange={(event)=>setEntryType(event.target.value as EntryType)}>
                        <option value="NEXT_ACTION">다음 행동</option>
                        <option value="TRANSITION_POINT">전환점</option>
                        <option value="MILESTONE">마일스톤</option>
                        <option value="TEACHER_COMMENT">교사 내부메모</option>
                      </select>
                    </label>
                    {entryType!=="TEACHER_COMMENT" && (
                      <label>
                        학생 공개
                        <select value={entryVisibility} onChange={(event)=>setEntryVisibility(event.target.value)}>
                          <option value="LEARNER_AND_STAFF">학생과 교사</option>
                          <option value="STAFF_ONLY">교사만</option>
                        </select>
                      </label>
                    )}
                    <label className="wide">
                      제목
                      <input value={entryTitle} onChange={(event)=>setEntryTitle(event.target.value)} />
                    </label>
                    <label className="wide">
                      내용
                      <textarea value={entryBody} onChange={(event)=>setEntryBody(event.target.value)} />
                    </label>
                    <button className="primary compact-button" disabled={busy!=="" || !entryTitle.trim()} onClick={createEntry}>
                      기록 추가
                    </button>
                  </div>

                  <div className="journey-entry-list">
                    {currentEntries.slice(0,8).map((entry)=>(
                      <article className={entry.visibility==="STAFF_ONLY" ? "journey-entry-card staff" : "journey-entry-card"} key={entry.entryId}>
                        <div>
                          <span className="journey-type-chip">{entryTypeLabel(entry.entryType)}</span>
                          {entry.visibility==="STAFF_ONLY" && <span className="status-pill">교사만</span>}
                        </div>
                        <strong>{entry.title}</strong>
                        <p>{entry.body || "내용 없음"}</p>
                        <small>{formatDateTime(entry.occurredAt)} · {entry.createdByName}</small>
                      </article>
                    ))}
                    {!currentEntries.length && <p className="case-empty">아직 추가 기록이 없습니다.</p>}
                  </div>
                </section>
              </div>

              <section className="journey-next-actions">
                <div className="panel-title">
                  <strong>현재 다음 행동</strong>
                  <span>{nextActions.length}건</span>
                </div>
                <div className="journey-next-grid">
                  {nextActions.map((entry)=>(
                    <article key={entry.entryId}>
                      <strong>{entry.title}</strong>
                      <p>{entry.body || "내용 없음"}</p>
                    </article>
                  ))}
                  {!nextActions.length && <p className="case-empty">등록된 다음 행동이 없습니다.</p>}
                </div>
              </section>

              {teacherComments.length>0 && (
                <section className="journey-staff-note-section">
                  <div className="panel-title">
                    <strong>교사 내부메모</strong>
                    <span>Wallet 비공개</span>
                  </div>
                  {teacherComments.map((entry)=>(
                    <div className="review-line" key={entry.entryId}>
                      <div>
                        <strong>{entry.title}</strong>
                        <span>{entry.body}</span>
                      </div>
                      <small>{formatDate(entry.occurredAt)}</small>
                    </div>
                  ))}
                </section>
              )}

              <section className="journey-timeline-section">
                <div className="panel-title">
                  <strong>진로성장 Timeline</strong>
                  <span>{detail.timeline.length} events</span>
                </div>
                <div className="longitudinal-timeline">
                  {detail.timeline.map((item,index)=>(
                    <TimelineRow item={item} key={item.eventType+"-"+item.resourceId+"-"+index} />
                  ))}
                  {!detail.timeline.length && <p className="case-empty">표시할 성장 기록이 없습니다.</p>}
                </div>
              </section>
            </>
          )}
        </div>
      </div>
    </section>
  );
}

function JourneyMetric({label,value}:{label:string;value:number|string}){
  return <div className="journey-metric"><span>{label}</span><strong>{value}</strong></div>;
}

function TimelineRow({item}:{item:TimelineItem}){
  return (
    <article className={"longitudinal-row event-"+item.eventType.toLowerCase()}>
      <div className="timeline-date">{formatDate(item.occurredAt)}</div>
      <div className="timeline-marker">
        <span />
      </div>
      <div className={item.visibility==="STAFF_ONLY" ? "timeline-card staff" : "timeline-card"}>
        <div className="timeline-card-head">
          <span className="journey-type-chip">{timelineLabel(item.eventType)}</span>
          {item.visibility==="STAFF_ONLY" && <span className="status-pill">교사만</span>}
        </div>
        <strong>{item.title}</strong>
        {item.description && <p>{item.description}</p>}
        {item.eventType==="COMPETENCY" && item.metadata?.score!=null && (
          <small>역량점수 {String(item.metadata.score)}</small>
        )}
        {item.eventType==="EVALUATION" && item.metadata?.normalizedScore!=null && (
          <small>정규화점수 {String(item.metadata.normalizedScore)}</small>
        )}
      </div>
    </article>
  );
}
